import { Box, Checkbox, FormControlLabel, Link, Stack, Tooltip, Typography } from "@mui/material"

import { money } from "./InvoiceTable"
import { describeRate, surchargeCents } from "./surcharge"

/**
 * The card fee on the totals, with the choice to leave it off.
 *
 * ── Why it is a choice and not a setting ────────────────────────────────────
 *
 * The fee is what it costs to collect by card, so it belongs on an invoice that
 * will be collected by card — and the same client is charged automatically one
 * month and pays by transfer the next. Ticked by default because the card is
 * the common case, and because a default of off is a setting somebody forgets
 * and then absorbs.
 *
 * ── Why the rate is spelled out ─────────────────────────────────────────────
 *
 * Stripe's price is not one number. A domestic card is 2.9% + 30c; a card
 * issued abroad adds 1.5%, charging in the client's currency adds another 1%,
 * and Invoicing takes 0.4% of what it collects. Somebody deciding whether to
 * pass it on is deciding about a real cost, and "3.3% + $0.30" is checkable
 * against Stripe's own pricing page in a way that "processing fee" is not.
 */
export default function SurchargeRow({ baseCents, settings, checked, onChange, currency = "USD" }) {
  const rate = {
    surchargeBps: Number(settings?.surcharge_bps) || 0,
    surchargeFixedCents: Number(settings?.surcharge_fixed_cents) || 0,
  }
  const configured = rate.surchargeBps > 0 || rate.surchargeFixedCents > 0

  /*
   * Nothing at all when no rate is set for this client.
   *
   * An unticked box labelled "$0.00" is a control that cannot do anything and
   * one more line to read past — and worse, it suggests the fee is zero rather
   * than that nobody has said what it is. The settings screen is where that gets
   * answered.
   */
  if (!configured) return null

  const fee = checked ? surchargeCents(baseCents, rate) : 0

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={checked}
              onChange={(event) => onChange(event.target.checked)}
              inputProps={{ "aria-label": "Pass the card processing fee on" }}
            />
          }
          label={
            /* `describeChild`, because without it MUI puts the tooltip's prose
               on the row as `aria-label` — which replaces the accessible name,
               so the label a screen reader reads out is a sentence of
               explanation and the rate itself is never announced. */
            <Tooltip
              describeChild
              title="What Stripe takes to collect this by card. It is added so the amount you receive is the amount invoiced."
            >
              <Typography variant="body2" color="text.secondary">
                Card fee ({describeRate(rate, currency)})
              </Typography>
            </Tooltip>
          }
        />
        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {money(fee, currency)}
        </Typography>
      </Stack>
      {checked ? (
        /* The point of the gross-up, said once. Without it the obvious reading
           of the number above is base × rate, which is not what it is. */
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4, mt: -0.5 }}>
          Covers the fee on the fee, so {money(baseCents, currency)} reaches you
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4, mt: -0.5 }}>
          You absorb it — {money(surchargeCents(baseCents, rate), currency)} comes out of this invoice
        </Typography>
      )}
    </Box>
  )
}

/** Where the rate is set, for a screen that has to say so. */
export function SurchargeHint({ onOpenSettings }) {
  return (
    <Typography variant="caption" color="text.secondary">
      No card fee is set for this client.{" "}
      {onOpenSettings ? (
        <Link component="button" type="button" variant="caption" onClick={onOpenSettings}>
          Set one
        </Link>
      ) : null}
    </Typography>
  )
}
